import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import SOLR_SEARCH_REST_CONFIG_PROJECTS from "../projects/solr_search_rest_config_projects";
import useSearchAPIRequest from "../../../../../hooks/use-search-api-request";
import { useIntl } from "react-intl";

// Create the overview context.
export const OverviewContext = React.createContext({});

const getEndpoint = (bundle) => {
  switch (bundle) {
    case "projects":
      return "projects";
    default:
      return false;
  }
};

const getConfig = (endpoint, language) => {
  let langPrefix = "";
  if (language === "en") {
    langPrefix = "/en"
  }
  switch (endpoint) {
    case "projects":
      return SOLR_SEARCH_REST_CONFIG_PROJECTS(langPrefix);
    default:
      throw new Error(`Unknown endpoint ${endpoint}`);
  }
};

// The OverviewProvider makes the context store accessible to children.
const OverviewProvider = ({ children, bundle }) => {
  const intl = useIntl();

  const endpoint = getEndpoint(bundle);
  const defaultConfig = getConfig(endpoint, intl.locale);

  const [currentFilters, setCurrentFilters] = useState(null);
  const [pagerSize, setPagerSize] = useState(defaultConfig.pagerSize || 10);
  const [currentPage, setCurrentPage] = useState(0);
  const [facetSelector, setFacetSelector] = useState("");
  const [currentSorting, setCurrentSorting] = useState("");
  const [searchQueryString, setSearchQueryString] = useState("");

  /**
   * Merge the default config with the current filters and sorting.
   * This config is put into the useSearchAPIRequest hook to perform the request.
   */
  const config = {
    ...defaultConfig,
    filters: currentFilters,
    sort: currentSorting,
    q: searchQueryString,
  };

  /**
   * Fetch data from the API using the query string.
   */
  const { data, loading, error, fetchMore } = useSearchAPIRequest(config);

  // Extract nodes from the result.
  const items = data?.search_results || [];

  // Extract metadata from the result.
  const metaData = {
    firstPageLoaded: !!data && typeof data.search_results_page === "number",
    currentPage: data?.search_results_page || 0,
    totalRows: data?.search_results_count || 0,
    perPage: pagerSize || 10,
    pageCount: data?.search_results_pages || 1,
  };

  const addFilter = (filterId, filterValue) => {
    setCurrentFilters((prevFilters) => {
      const newFilters = { ...prevFilters };
      if (typeof newFilters[filterId] === "undefined") {
        newFilters[filterId] = [filterValue];
      } else if (!newFilters[filterId].includes(filterValue)) {
        newFilters[filterId].push(filterValue);
      }

      return newFilters;
    });
  };

  const setFilter = (filterId, filterValues) => {
    setCurrentFilters((prevFilters) => {
      if (!filterValues || !filterValues.length) {
        throw new Error(
          "Param filterValues must be an array with at least one value."
        );
      }
      const newFilters = { ...prevFilters };
      newFilters[filterId] = filterValues;

      const resolvedFilters = resolveFacetDependencies(newFilters);

      return resolvedFilters;
    });
  };

  const removeFilter = (filterId, filterValue) => {
    setCurrentFilters((prevFilters) => {
      const removeAllValues =
        typeof filterValue === "undefined" || filterValue === null;
      if (typeof prevFilters[filterId] === "undefined") {
        return prevFilters;
      }
      const newFilters = { ...prevFilters };
      if (removeAllValues) {
        delete newFilters[filterId];
      } else {
        newFilters[filterId] = newFilters[filterId].filter(
          (value) => value !== filterValue
        );
        if (newFilters[filterId].length === 0) {
          delete newFilters[filterId];
        }
      }

      const resolvedFilters = resolveFacetDependencies(newFilters);

      return resolvedFilters;
    });
  };

  /**
   * Unset filters if their dependencies are not met.
   * This is used to ensure that facets with dependencies are only shown when their dependencies are met.
   *
   * @param {Object} filters - The current filters.
   * @returns {Object} - The resolved filters.
   */
  const resolveFacetDependencies = (filters) => {
    const newFilters = { ...filters };

    config.facets?.forEach((facet) => {
      const { id, dependencies } = facet;
      if (!dependencies || !dependencies.length) {
        return;
      }

      // Check if all dependencies are met
      const dependenciesMet = dependencies.every((dependency) => {
        const { key, value } = dependency;
        const currentFilterValues = newFilters[key];

        // If the dependency key is not set at all, dependency is not met
        if (!currentFilterValues || !currentFilterValues.length) {
          return false;
        }

        // Check if any of the required dependency values are present
        return value.some((requiredValue) =>
          currentFilterValues.includes(requiredValue)
        );
      });

      // If dependencies are not met, remove the filter for this facet
      if (!dependenciesMet && newFilters[id]) {
        delete newFilters[id];
      }
    });

    return newFilters;
  };

  const resetFilters = () => {
    setCurrentFilters(null);
  };

  const isFilterSet = (
    filterId,
    filterValue,
    filterObject = currentFilters
  ) => {
    const filterIsSet =
      filterObject &&
      typeof filterObject[filterId] !== "undefined" &&
      filterObject[filterId].length > 0;
    if (typeof filterValue === "undefined") {
      return filterIsSet;
    }
    return filterIsSet && filterObject[filterId].includes(filterValue);
  };

  const setSorting = (sortField, sortOrder) => {
    setCurrentSorting(`${sortField}:${sortOrder}`);
  };

  const resetSorting = () => {
    setCurrentSorting("");
  };

  // The context value provides all necessary values and functions.
  const value = {
    config,
    bundle,
    metaData,
    searchQueryString,
    setSearchQueryString,
    currentPage,
    setCurrentPage,
    fetchMore,
    currentFilters,
    addFilter,
    setFilter,
    removeFilter,
    resetFilters,
    isFilterSet,
    setPagerSize,
    items,
    loading,
    error,
    facets: data?.facets || [],
    facetSelector,
    setFacetSelector,
    currentSorting,
    setSorting,
    resetSorting,
  };

  return (
    <OverviewContext.Provider value={value}>
      {children}
    </OverviewContext.Provider>
  );
};

OverviewProvider.propTypes = {
  children: PropTypes.node.isRequired,
  bundle: PropTypes.string.isRequired,
};

export default OverviewProvider;
